/**
 * Adaptive Video Height Matcher
 * Only applies height matching when container gets BIGGER than baseline
 * Preserves current behavior for smaller screens/zoom out
 */

class AdaptiveVideoHeight {
  constructor() {
    this.baselineWidth = null;
    this.currentWidth = null;
    this.resizeObserver = null;
    this.rightPanel = null;
    this.rotationSection = null;
    this.isInitialized = false;
    
    // Default video aspect ratio (16:9 for most videos)
    this.videoAspectRatio = 16/9;
    
    // Threshold for considering container "bigger" (10% increase)
    this.expansionThreshold = 1.1;
  }

  /**
   * Initialize the adaptive video height system
   */
  init() {
    if (this.isInitialized) return;
    
    try {
      // Find required elements
      this.rightPanel = document.querySelector('.right-panel');
      this.rotationSection = document.querySelector('.rotation-section');
      
      if (!this.rightPanel || !this.rotationSection) {
        console.warn('AdaptiveVideoHeight: Required elements not found');
        return;
      }

      // Set baseline width (current container width)
      this.baselineWidth = this.rightPanel.offsetWidth;
      this.currentWidth = this.baselineWidth;
      
      console.log(`AdaptiveVideoHeight: Baseline width set to ${this.baselineWidth}px`);

      // Extract video aspect ratio from ImageKit iframe if possible
      this.extractVideoAspectRatio();

      // Set up ResizeObserver
      this.setupResizeObserver();
      
      // Initial check
      this.checkAndApplyHeightMatching();
      
      this.isInitialized = true;
      console.log('AdaptiveVideoHeight: Initialized successfully');
      
    } catch (error) {
      console.error('AdaptiveVideoHeight: Initialization failed', error);
    }
  }

  /**
   * Extract video aspect ratio from ImageKit iframe URL
   */
  extractVideoAspectRatio() {
    try {
      const iframe = this.rightPanel.querySelector('.rotation-video');
      if (iframe && iframe.src) {
        // For now, use default 16:9 ratio
        // In production, you could parse ImageKit metadata
        this.videoAspectRatio = 16/9;
        console.log(`AdaptiveVideoHeight: Using aspect ratio ${this.videoAspectRatio}`);
      }
    } catch (error) {
      console.warn('AdaptiveVideoHeight: Could not extract aspect ratio, using default');
    }
  }

  /**
   * Set up ResizeObserver to monitor container size changes
   */
  setupResizeObserver() {
    if (!window.ResizeObserver) {
      console.warn('AdaptiveVideoHeight: ResizeObserver not supported');
      return;
    }

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === this.rightPanel) {
          const newWidth = entry.contentRect.width;
          
          // Only process if width actually changed
          if (Math.abs(newWidth - this.currentWidth) > 5) {
            this.currentWidth = newWidth;
            this.checkAndApplyHeightMatching();
          }
        }
      }
    });

    this.resizeObserver.observe(this.rightPanel);
  }

  /**
   * Check if container is bigger than baseline and apply height matching accordingly
   */
  checkAndApplyHeightMatching() {
    if (!this.baselineWidth || !this.currentWidth) return;

    const expansionRatio = this.currentWidth / this.baselineWidth;
    const isExpanded = expansionRatio >= this.expansionThreshold;

    if (isExpanded) {
      // Container is bigger - apply height matching
      this.applyHeightMatching();
      console.log(`AdaptiveVideoHeight: Applied height matching (${this.currentWidth}px, ratio: ${expansionRatio.toFixed(2)})`);
    } else {
      // Container is normal/smaller - remove height matching
      this.removeHeightMatching();
      console.log(`AdaptiveVideoHeight: Removed height matching (${this.currentWidth}px, ratio: ${expansionRatio.toFixed(2)})`);
    }
  }

  /**
   * Apply height matching when container is expanded
   */
  applyHeightMatching() {
    // Calculate video height based on current container width and aspect ratio
    const videoHeight = this.currentWidth / this.videoAspectRatio;
    
    // Apply the calculated height to the rotation section
    document.documentElement.style.setProperty('--adaptive-section-height', `${videoHeight}px`);
    document.documentElement.style.setProperty('--adaptive-height-active', '1');
  }

  /**
   * Remove height matching and return to default behavior
   */
  removeHeightMatching() {
    // Remove custom height properties to restore default CSS behavior
    document.documentElement.style.removeProperty('--adaptive-section-height');
    document.documentElement.style.removeProperty('--adaptive-height-active');
  }

  /**
   * Cleanup function
   */
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    this.removeHeightMatching();
    this.isInitialized = false;
    
    console.log('AdaptiveVideoHeight: Destroyed');
  }
}

// Initialize when DOM is ready
function initAdaptiveVideoHeight() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const adaptiveHeight = new AdaptiveVideoHeight();
      adaptiveHeight.init();
      
      // Store instance globally for debugging
      window.adaptiveVideoHeight = adaptiveHeight;
    });
  } else {
    const adaptiveHeight = new AdaptiveVideoHeight();
    adaptiveHeight.init();
    
    // Store instance globally for debugging
    window.adaptiveVideoHeight = adaptiveHeight;
  }
}

// Auto-initialize
initAdaptiveVideoHeight();

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.querySelector('.rotation-section')) {
    console.log('AdaptiveVideoHeight: Section reloaded, reinitializing');
    
    // Cleanup existing instance
    if (window.adaptiveVideoHeight) {
      window.adaptiveVideoHeight.destroy();
    }
    
    // Reinitialize after a short delay
    setTimeout(() => {
      const adaptiveHeight = new AdaptiveVideoHeight();
      adaptiveHeight.init();
      window.adaptiveVideoHeight = adaptiveHeight;
    }, 100);
  }
});

console.log('🎬 Adaptive Video Height loaded. Use window.adaptiveVideoHeight for debugging.');